import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, ArrowRight, Home, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';

const coreRegions = [
  { name: 'Los Angeles', description: 'Serving neighborhoods across the city of Los Angeles.' },
  { name: 'Culver City', description: 'Property solutions for Culver City homeowners.' },
  { name: 'Inglewood', description: 'Real estate services throughout Inglewood.' },
  { name: 'Pasadena', description: 'Serving the Pasadena community and surrounding areas.' },
  { name: 'West LA', description: 'Solutions for West Los Angeles neighborhoods.' },
  { name: 'San Gabriel Valley', description: 'Covering the San Gabriel Valley region.' },
  { name: 'Valley Communities', description: 'Serving San Fernando Valley and beyond.' }
];

const extendedAreas = [
  // Column 1
  ['San Marino', 'Monterey Park', 'South Pasadena', 'Montebello', 'La Puente', 'Alhambra', 'Diamond Bar', 'Chino Hills', 'Rosemead', 'San Gabriel'],
  // Column 2
  ['Arcadia', 'Monrovia', 'Baldwin Park', 'Whittier', 'Hacienda Heights', 'Rowland Heights', 'Walnut', 'West Covina', 'Covina', 'El Monte'],
  // Column 3
  ['Temple City', 'Rancho Cucamonga', 'Azusa', 'Glendora', 'San Dimas', 'Chino', 'Ontario', 'Claremont', 'Montclair', 'La Verne']
];

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Locations', href: '/locations', current: true }
];

const LocationsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Locations | Pacific Crest Properties — Los Angeles Real Estate</title>
        <meta name="description" content="Pacific Crest Properties serves homeowners across Los Angeles, offering renovation services and fast property solutions in communities such as Pasadena, San Gabriel Valley, Culver City, Inglewood, and more." />
        <meta name="keywords" content="Los Angeles real estate, Pasadena home buyers, San Gabriel Valley renovation, Culver City property solutions, Inglewood real estate services" />
        <link rel="canonical" href="https://pacificcrestprop.com/locations" />
        <meta property="og:title" content="Locations | Pacific Crest Properties" />
        <meta property="og:description" content="We serve homeowners across Greater Los Angeles with real estate solutions and renovation services." />
        <meta property="og:url" content="https://pacificcrestprop.com/locations" />
      </Helmet>
      
      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>
      
      {/* Hero Section */}
      <section className="pt-8 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
            Our Coverage
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Where We Serve
          </h1>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-xl text-white/70 leading-relaxed max-w-3xl mx-auto">
            Pacific Crest Properties supports homeowners across a wide range of Los Angeles communities. Whether you&apos;re exploring renovation options or considering a fast, as-is property offer, our team provides solutions tailored to your area and goals.
          </p>
        </div>
      </section>

      {/* Core Regions Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
              Core Service Regions
            </h2>
            <div className="divider-gold mx-auto mb-6" />
            <p className="text-lg" style={{ color: '#3A5A85' }}>
              Serving a wide variety of neighborhoods with flexible property solutions and renovation services.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreRegions.map((region, index) => (
              <article
                key={index}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                    <MapPin className="w-6 h-6" style={{ color: '#1F3B60' }} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl mb-2" style={{ color: '#1F3B60' }}>
                      {region.name}
                    </h3>
                    <p className="text-sm" style={{ color: '#3A5A85' }}>
                      {region.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Extended Service Areas Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F3B60' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">
              Extended Service Areas
            </h2>
            <div className="divider-gold mx-auto mb-6" />
            <p className="text-lg text-white/70">
              We also serve homeowners in these additional communities throughout the greater Los Angeles region.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {extendedAreas.map((column, colIndex) => (
              <div key={colIndex} className="card-navy p-6">
                <ul className="space-y-3">
                  {column.map((area, areaIndex) => (
                    <li key={areaIndex} className="flex items-center space-x-3">
                      <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#D6A94F' }} />
                      <span className="text-white/80">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#3A5A85' }}>
            Whether you&apos;re looking to sell your property or explore renovation options, we&apos;re here to help.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/sell-your-home"
              className="btn-gold group flex items-center space-x-2 text-lg"
            >
              <Home className="w-5 h-5" />
              <span>Get Your Offer</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/renovation"
              className="btn-outline-gold group flex items-center space-x-2 text-lg"
              style={{ color: '#1F3B60', borderColor: '#1F3B60' }}
            >
              <Wrench className="w-5 h-5" />
              <span>Renovation Services</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Back to Home */}
          <div className="mt-12">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 font-medium transition-colors duration-300"
              style={{ color: '#3A5A85' }}
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationsPage;
